import { useEffect, useMemo, useState } from 'react';
import { Q } from '@nozbe/watermelondb';
import { Plus, Search, WalletCards } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { database } from '@/db';
import { Customer } from '@/db/models/Customers';
import { useAppStore } from '@/store/useAppStore';
import { useObservable } from '@/hooks/useObservable';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Dialog, DialogContent } from '@/components/ui/Dialog';
import { formatCurrency } from '@/utils/format';
import { cn } from '@/utils/cn';
import { RBACGate, CUSTOMER_CREATE } from '@/rbac';
import { CustomerForm } from './CustomerForm';

type Filter = 'ALL' | 'DUE';

export function CustomerListScreen() {
  const navigate = useNavigate();
  const activeStoreId = useAppStore((s) => s.activeStoreId);
  const [queryInput, setQueryInput] = useState('');
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('ALL');
  const [formOpen, setFormOpen] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setQuery(queryInput.trim().toLowerCase()), 180);
    return () => clearTimeout(t);
  }, [queryInput]);

  const customers = useObservable<Customer[]>(
    () => {
      if (!activeStoreId) return null;
      const conditions: any[] = [
        Q.where('store_id', activeStoreId),
        Q.where('is_deleted', false),
      ];
      if (query) {
        const like = `%${query}%`;
        conditions.push(Q.or(
          Q.where('name', Q.like(like)),
          Q.where('phone', Q.like(like)),
        ));
      }
      return database.get<Customer>('customers').query(...conditions, Q.sortBy('updated_at', Q.desc)).observe();
    },
    [activeStoreId, query],
    [],
  );

  const visible = useMemo(
    () => filter === 'DUE' ? customers.filter((c) => c.balance > 0) : customers,
    [customers, filter],
  );
  const totalDue = useMemo(() => customers.reduce((sum, c) => sum + Math.max(0, c.balance), 0), [customers]);

  if (!activeStoreId) return <div className="text-body text-fg-secondary">No active store. Sign in again.</div>;

  return (
    <div className="flex h-full flex-col gap-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-display text-fg-primary">Customers</h1>
          <p className="mt-1 text-body text-fg-secondary">
            {visible.length} customers · outstanding {formatCurrency(totalDue)}
          </p>
        </div>
        <RBACGate requires={CUSTOMER_CREATE}>
          <Button onClick={() => setFormOpen(true)}><Plus size={16} /> New customer</Button>
        </RBACGate>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative w-full max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-fg-tertiary" />
          <Input className="pl-9" placeholder="Search by name or phone" value={queryInput} onChange={(e) => setQueryInput(e.target.value)} />
        </div>
        <Segment label="All" active={filter === 'ALL'} onClick={() => setFilter('ALL')} />
        <Segment label="With due" active={filter === 'DUE'} onClick={() => setFilter('DUE')} />
      </div>

      <div className="flex-1 overflow-auto rounded-lg border border-stroke-hairline/10 bg-bg-surface">
        {visible.length === 0 ? (
          <div className="flex h-full items-center justify-center text-body text-fg-tertiary">
            {query ? 'No customers match this search.' : 'No customers yet.'}
          </div>
        ) : (
          <table className="w-full text-body">
            <thead className="sticky top-0 bg-bg-surface">
              <tr className="border-b border-stroke-hairline/10 text-label uppercase text-fg-tertiary">
                <th className="px-4 py-2.5 text-left">Customer</th>
                <th className="px-4 py-2.5 text-right">Spend</th>
                <th className="px-4 py-2.5 text-right">Visits</th>
                <th className="px-4 py-2.5 text-right">Due</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((customer) => (
                <tr
                  key={customer.id}
                  className="cursor-pointer border-b border-stroke-hairline/5 hover:bg-bg-subtle transition-colors"
                  onClick={() => navigate(`/customers/${customer.id}`)}
                >
                  <td className="px-4 py-3">
                    <div className="font-medium text-fg-primary">{customer.name || 'Unnamed customer'}</div>
                    <div className="text-label text-fg-tertiary">{customer.phone || customer.email || 'No contact saved'}</div>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">{formatCurrency(customer.totalSpend)}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{customer.visitCount}</td>
                  <td className="px-4 py-3 text-right">
                    <span className={cn(
                      'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-label tabular-nums',
                      customer.balance > 0 ? 'bg-warning/10 text-warning-ink' : 'bg-success/10 text-success-ink',
                    )}>
                      <WalletCards size={12} /> {formatCurrency(customer.balance)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent title="New customer" className="w-[720px]">
          <CustomerForm storeId={activeStoreId} onSaved={() => setFormOpen(false)} onCancel={() => setFormOpen(false)} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Segment({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-md px-3 py-1.5 text-body transition-colors',
        active ? 'bg-accent/10 text-accent-ink' : 'text-fg-secondary hover:bg-bg-subtle hover:text-fg-primary',
      )}
    >
      {label}
    </button>
  );
}
